import { useTranslation } from 'react-i18next';

interface PagerProps {
  total: number;
  page: number;
  pageSize: number;
  onPage: (p: number) => void;
  onPageSize: (s: number) => void;
}

const PAGE_SIZES = [5, 10, 25, 50] as const;

function buildPageRange(page: number, totalPages: number): (number | '...')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: (number | '...')[] = [1];
  if (page > 3) pages.push('...');
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (page < totalPages - 2) pages.push('...');
  pages.push(totalPages);
  return pages;
}

export function Pager({ total, page, pageSize, onPage, onPageSize }: PagerProps) {
  const { t } = useTranslation();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = Math.min((page - 1) * pageSize + 1, total);
  const rangeEnd = Math.min(page * pageSize, total);
  const pages = buildPageRange(page, totalPages);

  return (
    <div className="pager-wrap">
      <div className="pager-left">
        <span className="pager-size-label">{t('common.pagerShow')}</span>
        <select
          className="pager-size-select"
          value={pageSize}
          onChange={e => onPageSize(Number(e.target.value))}
          aria-label={t('common.pagerPerPage')}
        >
          {PAGE_SIZES.map(s => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <span className="pager-size-label">{t('common.pagerPerPage')}</span>
      </div>

      <div className="pager-controls">
        <button
          className="pager-btn"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1}
          aria-label={t('common.previousPage')}
        >
          <i className="fas fa-chevron-left" aria-hidden="true" />
        </button>

        {pages.map((p, idx) =>
          p === '...' ? (
            <span key={`ellipsis-${idx}`} className="pager-ellipsis">
              …
            </span>
          ) : (
            <button
              key={p}
              className={`pager-btn${p === page ? ' active' : ''}`}
              onClick={() => onPage(p)}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </button>
          ),
        )}

        <button
          className="pager-btn"
          onClick={() => onPage(page + 1)}
          disabled={page >= totalPages}
          aria-label={t('common.nextPage')}
        >
          <i className="fas fa-chevron-right" aria-hidden="true" />
        </button>
      </div>

      <span className="pager-info">
        {rangeStart}–{rangeEnd} {t('common.pagerOf')} {total}
      </span>
    </div>
  );
}
